import { Button, H2 } from '@/common/ui'
import { useCampaignPledgeFetcher, useSubscribeCampaign, useUnsubscribeCampaign } from '@/campaigns/campaignsQueries'
import { ReactNode, useEffect, useState, useMemo } from 'react'
import { CampaignT } from '@/common/types'
import { Heart, Share2 } from 'lucide-react'
import { CampaignShareModal } from './CampaignShareModal'
import clsx from 'clsx'
import { PledgedHoldings } from '@/portfolio/PledgedHoldings'
import { analytics } from '@/common/analytics/analytics'
import { CampaignStatusPill } from './CampaignStatusPill'
import { ExternalLink } from 'lucide-react'
import { CountdownTimer } from './CountdownTimer'
import { SecurityAvatar } from '@/securities/securityAvatar.tsx'
import { cn } from '@/common/utils/utils'
import { useCampaignsWithHoldings } from '@/dashboard/useCampaignsWithHoldings'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'
import { Link, useNavigate } from 'react-router-dom'

export type CampaignWithSubscription = CampaignT & { isSubscribed?: boolean; pledged?: boolean }

type Props = {
    campaign: CampaignWithSubscription
    handleOrder: () => void
    layout?: 'default' | 'expanded'
}

export const Campaign = ({ campaign, handleOrder, layout = 'default' }: Props) => {
    const subscribeCampaign = useSubscribeCampaign()
    const unsubscribeCampaign = useUnsubscribeCampaign()
    const { campaignsWithHoldings } = useCampaignsWithHoldings()
    const navigate = useNavigate()

    const [subscribed, setSubscribed] = useState<boolean>(campaign.isSubscribed ?? false)
    const [shared, setShared] = useState<boolean>(false)
    const [followers, setFollowers] = useState<number>(campaign.followers)
    const [allPledgesState, setAllPledgesState] = useState<{ [key: string]: number }>({})
    const [userPledgedSecurities, setPledgedSecurities] = useState<{ securityCode: string; securityName: string }[]>([])
    const pledgeQueries = useCampaignPledgeFetcher(campaign.campaignId, campaign.targetCompaniesAsxCodes)

    const handleFollow = () => {
        setSubscribed(true)
        setFollowers(followers + 1)
        subscribeCampaign.mutate(campaign.campaignId)
        analytics.track('Campaign Followed', {
            campaign_slug: campaign.campaignId,
            campaign_name: campaign.title,
            source_page: 'campaign',
        })
    }

    const handleUnfollow = () => {
        setSubscribed(false)
        setFollowers(followers - 1)
        unsubscribeCampaign.mutate(campaign.campaignId)
        analytics.track('Campaign Unfollowed', {
            campaign_slug: campaign.campaignId,
            campaign_name: campaign.title,
            source_page: 'campaign',
        })
    }

    const closeShareModal = () => {
        setShared(false)
    }

    const toggleFollow = () => {
        if (subscribed) {
            handleUnfollow()
        } else {
            handleFollow()
        }
    }

    const handleShare = () => {
        setShared(true)
        analytics.track('CampaignShareButton Clicked', {
            campaign_slug: campaign.campaignId,
            campaign_name: campaign.title,
            source_page: 'campaign',
        })
    }

    useEffect(() => {
        setSubscribed(campaign.isSubscribed ?? false)
    }, [campaign.isSubscribed])

    useEffect(() => {
        const pledgedCampaign = campaignsWithHoldings.find((c) => c.campaignId === campaign.campaignId)
        setPledgedSecurities(
            (pledgedCampaign?.pledgedCompanies || [])?.map((company) => {
                return {
                    securityCode: company.code,
                    securityName: company.name,
                }
            }),
        )
    }, [campaign, campaignsWithHoldings])

    // Derive pledges from react-query results; update backup state in an effect
    const { pledges: derivedPledges, settled } = useMemo(() => {
        if (!pledgeQueries || pledgeQueries.length === 0) return { pledges: {}, settled: false }
        const allSettled = pledgeQueries.every((q) => q.isSuccess || q.isError)
        if (!allSettled) return { pledges: {}, settled: false }

        const pledges: { [key: string]: number } = {}
        pledgeQueries.forEach((q, idx) => {
            const code = campaign.targetCompaniesAsxCodes[idx]
            if (q.isSuccess) pledges[code] = q.data ?? 0
            else pledges[code] = 0
        })
        return { pledges, settled: true }
    }, [pledgeQueries, campaign.targetCompaniesAsxCodes])

    useEffect(() => {
        if (!settled) return
        setAllPledgesState((prev) => {
            // shallow compare
            const prevKeys = Object.keys(prev)
            const nextKeys = Object.keys(derivedPledges)
            if (prevKeys.length === nextKeys.length && prevKeys.every((k) => prev[k] === derivedPledges[k])) {
                return prev
            }
            return derivedPledges
        })
    }, [settled, derivedPledges])

    const allPledges = settled ? derivedPledges : allPledgesState

    const CampaignHero = () => {
        return (
            <a href={`/campaigns/${campaign.campaignId}`} className="contents">
                <div
                    className={cn(
                        `relative bg-cover bg-center min-h-[192px] w-full`,
                        layout === 'expanded' ? 'md:w-[55%]' : 'md:w-full',
                    )}
                    style={{ backgroundImage: `url('${campaign.heroImage.url}')` }}
                >
                    <div
                        className={cn(
                            'absolute w-full flex items-end',
                            layout === 'default' ? 'max-w-[298px] bottom-0' : 'top-6',
                        )}
                    >
                        {campaign.countdownDate && (
                            <CountdownTimer countdownDate={campaign.countdownDate} className="px-6 pb-6" />
                        )}
                    </div>
                </div>
            </a>
        )
    }

    const onCampaignClick = () => {
        analytics.trackWithDefaults(ANALYTICS_EVENTS.CAMPAIGNS.CAMPAIGN_CLICKED, {
            campaign_id: campaign.campaignId,
            security_codes: campaign.targetCompaniesAsxCodes,
        })
        navigate(`/campaigns/${campaign.campaignId}`)
    }

    const CampaignDetails = () => {
        return (
            <div className="p-8 pb-0">
                {campaign.status && <CampaignStatusPill status={campaign.status} campaignId={campaign.campaignId} />}
                <Link
                    to={`/campaigns/${campaign.campaignId}`}
                    onClick={() =>
                        analytics.trackWithDefaults(ANALYTICS_EVENTS.CAMPAIGNS.CAMPAIGN_CLICKED, {
                            campaign_id: campaign.campaignId,
                            security_codes: campaign.targetCompaniesAsxCodes,
                        })
                    }
                    className="text-black"
                    aria-label={`View ${campaign.title} campaign`}
                >
                    <H2>{campaign.title}</H2>
                </Link>
                <div className="flex justify-between py-4 mb-2 mt-2 flex-col gap-2 xs:flex-row">
                    <Button variant="ghost" className="hover:bg-transparent px-4 md:px-0" onClick={toggleFollow}>
                        <Heart className={clsx({ 'text-destructive fill-destructive': subscribed })} />
                        <span>{followers} followers</span>
                    </Button>
                    <Button variant="outline" className="hover:bg-transparent" onClick={handleShare}>
                        <Share2 />
                        Share
                    </Button>
                </div>
                {shared && <CampaignShareModal campaign={campaign} onClose={closeShareModal} />}
                {campaign.description && (
                    <div className="pb-6">
                        <p className="mb-2">{campaign.description}</p>
                        <Button
                            size="sm"
                            variant="tertiary"
                            className="border-0 py-3 px-5 text-xs"
                            onClick={onCampaignClick}
                        >
                            Read more
                        </Button>
                    </div>
                )}
            </div>
        )
    }

    const CampaignSecurities = () => {
        return (
            <>
                {userPledgedSecurities.length > 0 && (
                    <div className="mb-2">
                        <PledgedHoldings pledges={userPledgedSecurities} />
                    </div>
                )}
                <div className="flex flex-col gap-1 mb-4">
                    {Object.keys(allPledges)
                        .sort()
                        .map((code) => {
                            if (allPledges[code] === 0) return null
                            return (
                                <div key={code} className="flex items-center mb-1">
                                    <div className="min-w-[30%] text-right mr-1 flex items-center">
                                        <SecurityAvatar className="mr-1" securityCode={code} height={8} width={8} />
                                        <span className="text-[13px]">{allPledges[code]}/100</span>
                                    </div>
                                    <div className="relative w-[60%] h-[8px] rounded-full overflow-hidden">
                                        <style>{`.${code} { --bar-width: ${Math.min(allPledges[code], 100)}%; }`}</style>
                                        <div className={`absolute bg-[#F0F0F0] w-full h-full ${code}`}>
                                            <div className={`bg-electric-lime w-[--bar-width] h-full`}></div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}{' '}
                </div>
            </>
        )
    }

    const CampaignActions = () => {
        return (
            <div>
                {campaign.targetCompaniesAsxCodes.length > 0 && (
                    <>
                        <Button onClick={handleOrder} tabIndex={-1} variant="secondary" fullWidth>
                            Buy Shares
                        </Button>
                        <div className="mt-3 text-center">
                            <a
                                href="https://www.six-invest.com.au/agency-agreement"
                                target="_blank"
                                className="text-xs text-accent-foreground flex justify-center items-center"
                            >
                                Pledge shares I own outside of SIX
                                <ExternalLink className="ml-1 h-3 w-3" />
                            </a>
                        </div>
                    </>
                )}
            </div>
        )
    }

    const ArticleLayout = ({ children }: { children: ReactNode }) => {
        return (
            <article
                key={campaign.campaignId}
                className={cn(
                    'w-full mb-10 rounded-md border flex overflow-hidden justify-between flex-col',
                    layout === 'expanded' ? 'md:flex-row-reverse' : 'md:flex-col',
                )}
            >
                {children}
            </article>
        )
    }

    if (layout === 'expanded')
        return (
            <ArticleLayout>
                <CampaignHero />
                <div className="w-full md:w-[45%]">
                    <CampaignDetails />
                    <div className="px-6 pb-8">
                        <CampaignSecurities />
                        <CampaignActions />
                    </div>
                </div>
            </ArticleLayout>
        )

    return (
        <ArticleLayout>
            <div>
                <CampaignHero />
                <div className="md:w-full">
                    <CampaignDetails />
                </div>
            </div>
            <div>
                <div className="px-6 pb-8">
                    <CampaignSecurities />
                    <CampaignActions />
                </div>
            </div>
        </ArticleLayout>
    )
}
